import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { FpcAuthenticateResponse, SystemApiService } from './api-services';
import { API_URL } from './injection-tokens';

export interface TokenData {
    accessToken: string;
    refreshToken: string;
}

export interface TokenDataExtraInfo {
  guid?: string | null;
  prefix?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly systemApiService = inject(SystemApiService);
  private readonly apiBaseUrl = inject(API_URL);

  private readonly storageKey = 'mainapp_jwttoken';

  private getBaseUrl(): string {
    return `${this.apiBaseUrl.replace(/\/$/, '')}/Token`;
  }

  getTokenData(): TokenData | null {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return null;

    try {
        const data: TokenData = JSON.parse(raw);
        return data;
    } catch { }

    return null;
  }

  getAccessToken(): string | null {
      return this.getTokenData()?.accessToken ?? null;
  }

  login(sourceId: string, lastName: string): Observable<boolean> {
    const payload = {
      sourceID: sourceId,
      lastName,
    };

    return this.systemApiService.authenticateFpc(payload).pipe(
      map((response) => {
        const token = this.resolveAccessToken(response);
        if (!token) {
          return false;
        }

        localStorage.setItem(this.storageKey, JSON.stringify(response));

        return true;
      }),
      catchError(() => of(false)),
    );
  }

  logout(): void {
    localStorage.removeItem(this.storageKey);
  }

  isAuthenticated(): Observable<boolean> {
    const baseUrl = this.getBaseUrl();
    return this.httpClient.get(`${baseUrl}/Authentication/ValidateToken`).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  getJsonTokenDataFromLoggedUser(): Observable<TokenDataExtraInfo>{
    const baseUrl = this.getBaseUrl();
    return this.httpClient
      .get<TokenDataExtraInfo>(`${baseUrl}/Authentication/GetTokenJsonData`);
  }

  refreshToken(tokenModel: TokenData): Observable<TokenData> {
    const baseUrl = this.getBaseUrl();
    return this.httpClient.post<TokenData>(`${baseUrl}/Authentication/RefreshToken`, tokenModel).pipe(
      tap((response) => {
        localStorage.setItem(this.storageKey, JSON.stringify(response));
      }),
    );
  }

  private resolveAccessToken(response: FpcAuthenticateResponse): string | undefined {
    if (typeof response.accessToken === 'string' && response.accessToken.length > 0) {
      return response.accessToken;
    }

    const tokenCandidate = response['token'];
    if (typeof tokenCandidate === 'string' && tokenCandidate.length > 0) {
      return tokenCandidate;
    }

    return undefined;
  }
}
